// src/oura/hooks/useMyChangeRequests.ts
//
// The merchant's own requests, for "Your requests" on Work: everything they
// started (YW, 29 Sep 2026). What they sent with Ask for a change, where
// each one is and our reply; and the calls they asked for, and whether each
// is booked.
//
// Read when Work opens, again when the tab comes back to the front, every 60
// seconds while it is in front (a reply lands whenever Angel sends it), and
// on demand (the bell's link to a reply). A request this tab sends shows at
// once, before the next read: the service announces it (requestChange, or followUpChangeRequest, #4930).
//
// Absent means unknown. `notServed` (the route is not live, or the switch is
// off) shows no list at all, never an empty one; a failed first read says it
// failed; a failed later read keeps the last good list. The calls are their
// own read (route 19): not served, they are left out; failed, the last good
// ones stay and the card says it could not check.

import { useCallback, useEffect, useRef, useState } from "react";
import { readMyCallRequests, readMyChangeRequests, subscribeMyChangeRequests, type MyCallRequest, type MyChangeRequest } from "@service/aeo/clientReview";

const EVERY = 60_000;

export type MyRequestsRead =
    | { state: "loading" }
    | { state: "notServed" }
    | { state: "failed" }
    /**
     * `refreshFailed`: the last read of either list failed, and what shows is
     * the last good read. `calls` is [] where route 19 is not served.
     */
    | { state: "ready"; requests: MyChangeRequest[]; calls: MyCallRequest[]; refreshFailed: boolean };

export interface MyRequests {
    read: MyRequestsRead;
    /** Read again now. Resolves once that read is back, good or failed. */
    refresh: () => Promise<void>;
}

const time = (iso: string | null | undefined) => (iso ? Date.parse(iso) || 0 : 0);
// Newest activity first: a follow-up or reply moves a request up (#4930).
const newestFirst = (a: MyChangeRequest, b: MyChangeRequest) => time(b.lastAt ?? b.at) - time(a.lastAt ?? a.at);

/** A row the read has not caught up with yet goes in front; the read's own copy wins once it has it. */
function merge(list: MyChangeRequest[], sent: ReadonlyMap<string, MyChangeRequest>): MyChangeRequest[] {
    const extra = Array.from(sent.values()).filter((s) => !list.some((r) => r.id === s.id));
    return extra.length ? [...extra, ...list].sort(newestFirst) : list;
}

/** `enabled` false (a staffer's preview, no Ask for a change) reads nothing and answers notServed. */
export function useMyChangeRequests(enabled: boolean): MyRequests {
    const [read, setRead] = useState<MyRequestsRead>(enabled ? { state: "loading" } : { state: "notServed" });
    /** Sent from this tab and not yet in a read. */
    const sent = useRef(new Map<string, MyChangeRequest>());
    const alive = useRef(true);
    /** Only the newest read may settle the list, so an older one that comes back late never wins. */
    const seq = useRef(0);

    const refresh = useCallback(async () => {
        if (!enabled) return;
        const mine = ++seq.current;
        const [r, c] = await Promise.all([readMyChangeRequests(), readMyCallRequests()]);
        if (!alive.current || mine !== seq.current) return;
        const callsFailed = !c.ok && c.reason !== "notServed";
        if (r.ok) {
            for (const row of r.data) sent.current.delete(row.id);
            setRead((prev) => ({
                state: "ready",
                requests: merge(r.data, sent.current),
                // A failed calls read keeps the last good calls; not served, there are none to show.
                calls: c.ok ? c.data : callsFailed && prev.state === "ready" ? prev.calls : [],
                refreshFailed: callsFailed,
            }));
        } else if (r.reason === "notServed") setRead({ state: "notServed" });
        else setRead((prev) => (prev.state === "ready" ? { ...prev, calls: c.ok ? c.data : prev.calls, refreshFailed: true } : { state: "failed" }));
    }, [enabled]);

    useEffect(() => {
        alive.current = true;
        if (!enabled) {
            setRead({ state: "notServed" });
            return () => {
                alive.current = false;
            };
        }
        // A read still out when this ends (switched off, or gone) must not land
        // after the next run has set `alive` again: it is no longer the newest.
        const invalidate = () => {
            seq.current += 1;
        };
        void refresh();
        const offSent = subscribeMyChangeRequests((row) => {
            sent.current.set(row.id, row);
            // Only into a list already read: on its own it would pass for the whole list.
            setRead((prev) => (prev.state === "ready" ? { ...prev, requests: merge(prev.requests.filter((r) => r.id !== row.id), sent.current) } : prev));
            void refresh();
        });
        const again = () => {
            if (document.visibilityState === "visible") void refresh();
        };
        const timer = window.setInterval(again, EVERY);
        document.addEventListener("visibilitychange", again);
        return () => {
            alive.current = false;
            invalidate();
            offSent();
            window.clearInterval(timer);
            document.removeEventListener("visibilitychange", again);
        };
    }, [enabled, refresh]);

    return { read, refresh };
}
